import { cloneDeep, compact, filter, find, includes, isEmpty, map, some, omit, pick } from 'lodash'
import { createActions } from 'redux-actions'

import { usernameSelector } from 'selectors/users'
import { currentBoard, currentComponent, currentComponents, currentComponentType,
  currentI2CSettings, currentWipper, currentWipperConnected,
  hasWipperByDeviceId, nextComponentKey, currentComponentHasCosmeticChangesOnly } from 'selectors/wprsnpr'
import GroupActions from 'actions/groups'
import FeedActions from 'actions/feeds'
import DataActions from 'actions/data'

const WprSnpr = {
  ...createActions({ },
    "WPRSNPR_CLEAR_CHECKIN",
    "WPRSNPR_HIDE_POPOVER",
    "WPRSNPR_CONNECTIONS",
    "WPRSNPR_CLEAR_I2C_ADDRESSES",
  ),

  refreshDevice: group_key => (dispatch, getState) => {
    const username = usernameSelector(getState())

    // refresh this device
    dispatch(GroupActions.get({ username, group_key }))
  },

  wprsnprCheckin: payload => (dispatch, getState) => {
    const username = usernameSelector(getState())

    // ensure groups have been fetched
    dispatch(GroupActions.all({ username }))
      .then(() => {
        // check if we already have this wipper
        const wipperIsNew = !hasWipperByDeviceId(getState(), payload.deviceId)

        if(wipperIsNew) {
          // conditionally do the checkin process for new wippers
          dispatch({ type: 'WPRSNPR_CHECKIN', payload })
        }
      })
  },

  setCurrentComponent: payload => dispatch => {
    dispatch(WprSnpr.clearCurrentComponents())
    dispatch({ type: 'WPRSNPR_SET_CURRENT_COMPONENT', payload })
  },

  setCurrentComponents: payload => dispatch => {
    dispatch(WprSnpr.clearCurrentComponent())
    dispatch({ type: 'WPRSNPR_SET_CURRENT_COMPONENTS', payload })
  },

  updateCurrentComponent: payload => (dispatch, getState) => {
    const componentToUpdate = currentComponent(getState())
    if(!componentToUpdate) { return }

    dispatch({
      type: 'WPRSNPR_SET_CURRENT_COMPONENT',
      payload: {
        ...componentToUpdate,
        ...payload,
        wipper_pin_info: {
          ...componentToUpdate.wipper_pin_info,
          ...payload.wipper_pin_info
        }
      }
    })
  },

  updateCurrentComponentsProperty: (propertyName, propertyValue) => (dispatch, getState) => {
    const componentsToUpdate = currentComponents(getState())
    if(componentsToUpdate.length === 0) { return }

    dispatch(WprSnpr.setCurrentComponents(
      componentsToUpdate.map(subcomponent => ({
        ...subcomponent,
        [propertyName]: propertyValue // update every subcomponent's i2cAddress
      }))
    ))
  },

  saveCurrentComponent: () => (dispatch, getState) => {
    const
      componentType = currentComponentType(getState()),
      { isPin, isI2C, isServo, isDS18X20 } = componentType,
      saveAction = (isPin && WprSnpr.saveCurrentPinComponent)
        || (isI2C && WprSnpr.saveCurrentI2CComponent)
        || (isServo && WprSnpr.saveCurrentServoComponent)
        || (isDS18X20 && WprSnpr.saveCurrentDS18X20Component)

    if(!saveAction) { throw new Error(`No 'save' action for component type: ${componentType.type}`) }

    return dispatch(saveAction())
  },

  saveCurrentI2CComponent: () => (dispatch, getState) => {
    const
      state = getState(),
      components = currentComponents(state),
      deviceKey = currentWipper(state).key,
      i2cAddress = components[0]?.i2cAddress

    // save feeds
    // each subcomponent makes an API request
    return Promise.all(components.map(subcomponent => {
      // create/update/delete a feed
      const
        // translate from redux to rails/db
        component = {
          ...pick(subcomponent, ["name", "key"]),
          wipper_pin_info: {
            found: !!subcomponent.found,
            isI2C: true,
            ...pick(subcomponent, ["type", "period", "i2cAddress", "sensorType"])
          }
        },
        exists = !isEmpty(subcomponent.key),
        { enabled, sensorType } = subcomponent,
        actionToTake = exists ?
          (enabled ?
            "update" :
            "destroy") :
          "create"

      if(!exists) {
        if(!enabled) { return Promise.resolve() }

        // Looks like: ws-0x38-ambient-temp
        component.key = `ws-0x${i2cAddress.toString(16)}-${sensorType}`
      }

      // need the key in the closure for reference in .then()
      const key = includes(component.key, '.') ? // if it is a compound key
        component.key : // use it, otherwise
        // build the compound key
        `${deviceKey}.${component.key}`

      return (actionToTake === "create"
        ? dispatch(WprSnpr.createComponent(component))
        : actionToTake === "update"
          ? dispatch(WprSnpr.updateComponent(component))
          : dispatch(WprSnpr.destroyComponent(component.key))
      ).then(() => ({ action: actionToTake, key, sensorType }))
    }))
      .then(compact)
      .then(actions => {
        // group the keys by action
        const
          batch = {
            // array of keys to create
            "create": map(filter(actions, ['action', 'create']), "key"),
            // ...update
            "update": map(filter(actions, ['action', 'update']), "key"),
            // ...and sensorTypes to delete
            "destroy": map(filter(actions, ['action', 'destroy']), "sensorType"),
          },
          i2cPort = currentI2CSettings(state),
          payload = { batch, i2cPort, i2cAddress }

        // send a batch update to the device
        return dispatch(DataActions.publishToWipperI2CComponentAction(deviceKey, "batch", payload))
      })
  },

  saveCurrentPinComponent: () => (dispatch, getState) => {
    const
      state = getState(),
      // clone so we don't mutate state, API response does that for us
      componentToSave = cloneDeep(currentComponent(state)),
      creating = isEmpty(componentToSave.key)

    // new pin components...
    if(creating) {
      // get flagged as pin components
      componentToSave.wipper_pin_info.isPin = true
      // and need to generate the key
      componentToSave.key = nextComponentKey(state)
    }

    // Default the component name to reflect the pin name
    if(isEmpty(componentToSave.name)) {
      const
        { pinName } = componentToSave.wipper_pin_info,
        boardDefinition = currentBoard(state),
        { digitalPins, analogPins } = boardDefinition.components,
        pins = digitalPins.concat(analogPins),
        pinDefinition = find(pins, { name: pinName })

      componentToSave.name = pinDefinition?.displayName
    }

    return creating
      ? dispatch(WprSnpr.createPinComponent(componentToSave))
      : dispatch(WprSnpr.updatePinComponent(componentToSave))
  },

  saveCurrentServoComponent: () => (dispatch, getState) => {
    const
      state = getState(),
      // clone so we don't mutate state, API response does that for us
      componentToSave = cloneDeep(currentComponent(state)),
      creating = isEmpty(componentToSave.key)

    // New servos...
    if(creating) {
      // don't work until they're found
      componentToSave.wipper_pin_info.found = false
      // gets flagged as a servo
      componentToSave.wipper_pin_info.isServo = true
      // and need a unique key
      componentToSave.key = nextComponentKey(state)
    }

    // Default the component name to reflect the pin name
    if(isEmpty(componentToSave.name)) {
      const
        { pinName } = componentToSave.wipper_pin_info,
        boardDefinition = currentBoard(state),
        { digitalPins, analogPins } = boardDefinition.components,
        pins = digitalPins.concat(analogPins),
        pinDefinition = find(pins, { name: pinName })

      componentToSave.name = pinDefinition?.displayName
    }

    return creating
      ? dispatch(WprSnpr.createServoComponent(componentToSave))
      : dispatch(WprSnpr.updateServoComponent(componentToSave))
  },

  saveCurrentDS18X20Component: () => (dispatch, getState) => {
    const
      state = getState(),
      components = currentComponents(state),
      deviceKey = currentWipper(state).key,
      { pinName, sensorResolution } = (components[0] || {}),
      // if any component already exists, we need to deinit before we init
      useDeinit = some(components, "key")

    if(!pinName) { throw new Error("Pin is required.") }

    // save feeds
    // each subcomponent makes an API request
    return Promise.all(components.map(subcomponent => {
      // create/update/delete a feed
      const
        // translate from redux to rails/db
        component = {
          ...pick(subcomponent, ["name", "key"]),
          wipper_pin_info: {
            found: !!subcomponent.found,
            isDS18X20: true,
            ...pick(subcomponent, ["type", "sensorType", "pinName", "sensorResolution", "period"])
          }
        },
        exists = !isEmpty(subcomponent.key),
        { enabled, sensorType } = subcomponent,
        actionToTake = exists ?
          (enabled ?
            "update" :
            "destroy") :
          "create"

      // Doesn't exist and wasn't added, skip
      if(!(exists || enabled)) { return Promise.resolve() }
      if(!sensorType) { throw new Error("Sensor Type is required.") }

      // for new components, make a key from particular properties
      exists || (component.key = `ws-${pinName.toLowerCase()}-${sensorType}`)

      // need the key in the closure for reference in .then()
      const key = includes(component.key, '.') ? // if it is a compound key
        component.key : // use it, otherwise
        // build the compound key
        `${deviceKey}.${component.key}`

      return (actionToTake === "create"
        ? dispatch(WprSnpr.createComponent(component))
        : actionToTake === "update"
          ? dispatch(WprSnpr.updateComponent(component))
          : dispatch(WprSnpr.destroyComponent(component.key))
      ).then(() => (actionToTake !== "destroy") && key) // just create and update keys
    }))
      .then(compact)
      .then(keys => {
        // global info and keys to init
        const payload = { useDeinit, pinName, sensorResolution, keys }
        // send the ds18x20 update
        return dispatch(DataActions.publishToWipperComponentAction(deviceKey, null, "updateDS18X20", payload))
      })
  },

  createPinComponent: wipperData => (dispatch, getState) => {
    const deviceKey = currentWipper(getState()).key

    return dispatch(WprSnpr.createComponent(wipperData))
      .then(componentKey => {
        // early out if device not connected
        if(!currentWipperConnected(getState())) { return componentKey }
        // communicates with wipper device
        dispatch(DataActions.publishToWipperComponentAction(deviceKey, componentKey, "add"))

        return componentKey
      })
  },

  updatePinComponent: wipperData => (dispatch, getState) => {
    const
      state = getState(),
      deviceKey = currentWipper(state)?.key,
      componentKey = wipperData.key,
      notConnected = !currentWipperConnected(state),
      onlyCosmetic = currentComponentHasCosmeticChangesOnly(state),
      getApiPromise = () => dispatch(WprSnpr.updateComponent(wipperData))

    return (notConnected || onlyCosmetic) ?
      // no device connected, or purely cosmetic changes? just hit the api and return
      getApiPromise() :
      // coordinate the device to remove and re-add the component around the update
      new Promise(resolve => {
        // remove from connected device
        resolve(dispatch(DataActions.publishToWipperComponentAction(deviceKey, componentKey, "remove")))
      })// wait a moment
        .then(() => new Promise(resolve => setTimeout(resolve, 400)))
        // call the Rails API
        .then(getApiPromise)
        // add back to the connected device
        .then(() => dispatch(DataActions.publishToWipperComponentAction(deviceKey, componentKey, "add")))
  },

  createServoComponent: wipperData => (dispatch, getState) => {
    const
      deviceKey = currentWipper(getState()).key,
      username = usernameSelector(getState())

    return dispatch(WprSnpr.createComponent(wipperData))
      .then(componentKey => {
        // autopublish first data point at minPulseWidth so servo, slider, and data align
        const initialValue = wipperData.wipper_pin_info.minPulseWidth || 500
        return dispatch(DataActions.create({ feed_key: componentKey, datum: { value: initialValue }, username }))
          .then(() => {
            // early out if device not connected
            if(!currentWipperConnected(getState())) { return componentKey }
            // tell broker the device can attach this servo
            dispatch(DataActions.publishToWipperComponentAction(deviceKey, componentKey, "attachServo"))

            return componentKey
          })
      })
  },

  updateServoComponent: wipperData => (dispatch, getState) => {
    const
      deviceKey = currentWipper(getState())?.key,
      componentKey = wipperData.key,
      notConnected = !currentWipperConnected(getState()),
      getApiPromise = () => dispatch(WprSnpr.updateComponent(wipperData))
    return notConnected ?
      // no device connected, just hit the api and return
      getApiPromise() :
      // coordinate the device to remove and re-add the component around the update
      new Promise(resolve => {
        // detach from connected device
        resolve(dispatch(DataActions.publishToWipperComponentAction(deviceKey, componentKey, "detachServo")))
      })// wait a moment
        .then(() => new Promise(resolve => setTimeout(resolve, 400)))
        // call the Rails API
        .then(getApiPromise)
        // reattach to the connected device
        .then(() => dispatch(DataActions.publishToWipperComponentAction(deviceKey, componentKey, "attachServo")))
  },

  createComponent: wipperData => (dispatch, getState) => {
    const deviceKey = currentWipper(getState())?.key

    // calls Rails API
    return dispatch(FeedActions.create({
      feed: {
        group_ids: deviceKey,
        ...wipperData
      }
    })).then(response => {
      const componentKey = response.value?.object?.key
      // bad response from API
      if(!componentKey) {
        const error = response.value?.object?.error?.join(', ')
        throw new Error(error || `Invalid API response: ${JSON.stringify(response)}`)
      }

      return componentKey
    })
  },

  updateComponent: wipperData => dispatch => {
    return dispatch(FeedActions.replace({
      feed_key: wipperData.key,
      feed: omit(wipperData, ["key"])
    }))
  },

  destroyComponent: key => (dispatch, getState) => {
    const
      feed_key = key,
      username = usernameSelector(getState())

    return dispatch(FeedActions.destroy({ username, feed_key }))
  },

  clearCurrentComponent: () => dispatch => {
    dispatch({ type: 'WPRSNPR_SET_CURRENT_COMPONENT', payload: null })
  },

  clearCurrentComponents: () => dispatch => {
    dispatch({ type: 'WPRSNPR_SET_CURRENT_COMPONENTS', payload: [] })
  },

  clearCurrentComponentType: () => dispatch => {
    dispatch(WprSnpr.updateCurrentComponent({ wipper_pin_info: { type: null } }))
    dispatch(WprSnpr.clearCurrentComponents())
  },

  i2cBusError: busError => (dispatch, getState) => {
    const { type, deviceId, i2cAddress, message } = busError

    dispatch({ type: 'WPRSNPR_I2C_BUS_ERROR', payload: busError })

    // type tells what was happening when the error occurred, act appropriately
    switch(type) {
      case "scan":
        return dispatch({ type: 'WPRSNPR_SET_I2C_SCANNING', payload: false })

      case "deviceInit":
        // TODO: display an explanation why it was deleted
        break

      // These shouldn't be happening yet:
      // case "deviceUpdate":
      // case "deviceDeinit":

      default:
        throw new Error(`Unrecognized Bus Error Type: ${type}: ${message}`)
    }
  },

  i2cClearBusError: () => dispatch => {
    dispatch({ type: 'WPRSNPR_I2C_BUS_ERROR', payload: {} })
  },

  startI2CScan: i2cPortSettings => (dispatch, getState) => {
    const state = getState()
    i2cPortSettings = i2cPortSettings || currentI2CSettings(state)

    if(!i2cPortSettings) { throw new Error('No I2C Port Settings given or found for scan') }

    const key = currentWipper(state)?.key
    if(!key) { throw new Error('Cannot start I2C scan without a device selected') }

    // Clear what we have
    dispatch({ type: 'WPRSNPR_CLEAR_I2C_ADDRESSES' })
    dispatch(WprSnpr.i2cClearBusError())
    dispatch({ type: 'WPRSNPR_SET_I2C_SCANNING', payload: true })

    // Tell the broker to start the scan
    // (Comment this line out if using a mock, below)
    dispatch(DataActions.publishToWipperDeviceAction(key, "i2cScanAll", i2cPortSettings))

    // setTimeout(() => {
    //   // Mock a bus scan response
    //   dispatch(WprSnpr.setI2cAddressesFound([ 0x38, 0x76, 0x61, 0x20, 0x12 ]))
    //
    //   // Mock a bus scan error
    // }, 800)
    //   dispatch(WprSnpr.i2cBusError({ type: "scan", message: "THIS IS A BUS ERROR" }))
  },

  setI2cAddressesFound: addressesFound => dispatch => {
    dispatch({ type: 'WPRSNPR_SET_I2C_ADDRESSES', payload: addressesFound })
    dispatch({ type: 'WPRSNPR_SET_I2C_SCANNING', payload: false })
  }
}

export default WprSnpr
